import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, ShoppingCart, Minus, Plus, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGameBadgeClass = (game) => {
  switch (game) {
    case 'yugioh': return 'badge-yugioh';
    case 'magic': return 'badge-magic';
    case 'pokemon': return 'badge-pokemon';
    default: return 'bg-gray-600';
  }
};

const getGameLabel = (game) => {
  switch (game) {
    case 'yugioh': return 'Yu-Gi-Oh!';
    case 'magic': return 'Magic: The Gathering';
    case 'pokemon': return 'Pokémon';
    default: return game;
  }
};

const getCategoryLabel = (category) => {
  switch (category) {
    case 'deck': return 'Deck';
    case 'booster': return 'Booster';
    case 'single': return 'Carte à l\'unité';
    default: return category;
  }
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch {
        toast.error('Produit non trouvé');
      } finally {
        setLoading(false);
      }
    };

    const checkFavorite = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/favorites`, { withCredentials: true });
        setIsFavorite(response.data.products.some(p => p.product_id === id));
      } catch {
        // Ignore
      }
    };

    fetchProduct();
    checkFavorite();
  }, [id, user]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Connectez-vous pour ajouter au panier');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(product.product_id, quantity);
      toast.success(`${quantity} × ${product.name} ajouté au panier`);
    } catch {
      toast.error('Erreur lors de l\'ajout au panier');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!user) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${product.product_id}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${product.product_id}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Ajouté aux favoris');
      }
    } catch {
      toast.error('Erreur');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: product.name,
        text: `Découvrez ${product.name} sur TCG Shop`,
        url: window.location.href
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex flex-col items-center justify-center">
        <p className="text-gray-400 text-xl mb-4">Produit non trouvé</p>
        <Link to="/shop">
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-500">
            Retour à la boutique
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link to="/shop" className="inline-flex items-center text-gray-400 hover:text-white transition-colors" data-testid="back-to-shop">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la boutique
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden bg-[#121212] border border-white/10">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${getGameBadgeClass(product.game)}`}>
                {getGameLabel(product.game)}
              </span>
              {product.rarity && (
                <span className="px-3 py-1 bg-black/70 backdrop-blur-sm rounded-lg text-sm text-fuchsia-400 font-medium uppercase tracking-wider">
                  {product.rarity}
                </span>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <div className="flex-1">
              <p className="text-fuchsia-400 text-sm font-semibold uppercase tracking-wider mb-2">
                {getCategoryLabel(product.category)}
              </p>
              
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-4" data-testid="product-name">
                {product.name}
              </h1>

              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                {product.description}
              </p>

              <div className="flex items-center gap-4 mb-8">
                <span className="text-4xl font-bold text-fuchsia-400" data-testid="product-price">
                  {product.price.toFixed(2)} €
                </span>
                <span className={`text-sm ${product.stock > 5 ? 'text-green-400' : product.stock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {product.stock > 5 ? 'En stock' : product.stock > 0 ? `Plus que ${product.stock} en stock` : 'Rupture de stock'}
                </span>
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-gray-400">Quantité</span>
                <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="text-gray-400 hover:text-white"
                    data-testid="decrease-quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-12 text-center text-white font-semibold" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="text-gray-400 hover:text-white"
                    data-testid="increase-quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handleAddToCart}
                disabled={product.stock === 0 || addingToCart}
                className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-6 text-lg glow-primary"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {addingToCart ? 'Ajout...' : 'Ajouter au panier'}
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={handleFavoriteToggle}
                className={`border-white/20 py-6 ${isFavorite ? 'text-fuchsia-400 border-fuchsia-400' : 'text-white'}`}
                data-testid="favorite-btn"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={handleShare}
                className="border-white/20 text-white py-6"
                data-testid="share-btn"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            {/* Info */}
            <div className="mt-8 p-6 bg-[#121212] rounded-xl border border-white/10">
              <h3 className="text-white font-semibold mb-4">Informations</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Jeu</p>
                  <p className="text-white">{getGameLabel(product.game)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Catégorie</p>
                  <p className="text-white">{getCategoryLabel(product.category)}</p>
                </div>
                {product.rarity && (
                  <div>
                    <p className="text-gray-500">Rareté</p>
                    <p className="text-white capitalize">{product.rarity}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Stock</p>
                  <p className="text-white">{product.stock} unités</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
